import { useState } from 'react';
import {
  Box,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { styled } from '@mui/material/styles';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { Select } from '@liminal/web-ui/forms/Select';
import { SegmentedToggle } from '@liminal/web-ui/forms/SegmentedToggle';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { Snackbar } from '@liminal/web-ui/feedback/Snackbar';
import { useSnackbar } from '@liminal/web-ui/feedback/useSnackbar';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import {
  useRestartUserLifecycle,
  useSendNudgePreview,
  useUserDetail,
  useUserLifecycle,
} from '@/hooks/useUsers';
import { formatDate, formatOrderDate } from '@liminal/utils/date';
import { CHANNEL_POLICY_LABELS, sequenceLabel, sequenceStepLabel } from '@/lib/nudges';
import {
  LIFECYCLE_JOURNEY,
  lifecycleJourneyIndex,
  lifecycleOutcomeVariant,
  lifecycleStageDescription,
  lifecycleStageLabel,
  lifecycleStageVariant,
  onboardingGoalLabel,
} from '@/components/ui/statusHelpers';
import type { SendNudgePreviewRequest, UserLifecycle } from '@/api/types';
import { getErrorMessage } from '@/errors/messages';

// The onboarding nudge engine's view of one user (LIM-3551): where they are,
// what went out, what is planned, plus two staff tools — restart a test
// account's journey, and preview any message on your own account.

const DetailGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: theme.spacing(2),
}));

const PreviewRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: theme.spacing(2),
  alignItems: 'end',
}));

const DASH = '—';

function when(value?: string): string {
  return value ? formatOrderDate(value) : DASH;
}

const PREVIEW_SEQUENCES = [
  { value: 'verify', label: 'Verify' },
  { value: 'verify_setup', label: 'Verify (sign-up not finished)' },
  { value: 'review_wait', label: 'Review taking longer' },
  { value: 'fund', label: 'Fund' },
  { value: 'first_action', label: 'First action' },
  { value: 'adopt_savings', label: 'Adopt: savings' },
  { value: 'adopt_investing', label: 'Adopt: investing' },
  { value: 'winback_out', label: 'Win-back' },
] as const;

const PREVIEW_STEPS = Array.from({ length: 8 }, (_, i) => ({ value: String(i), label: `Step ${i + 1}` }));

const PREVIEW_GOALS = [
  { value: 'generic', label: 'No goal' },
  { value: 'investing', label: 'Investing' },
  { value: 'saving', label: 'Saving' },
  { value: 'transfers', label: 'Transfers' },
] as const;

type PreviewChannel = 'push' | 'email';

const PREVIEW_CHANNELS = [
  { value: 'push', label: 'Push' },
  { value: 'email', label: 'Email' },
] as const;

/** When the user finished each journey step, with the product for the two product steps. */
function stepDoneCaption(data: UserLifecycle, stepIndex: number): string | undefined {
  const done = [
    data.verifyTime && formatDate(data.verifyTime, { month: 'short' }),
    data.fundTime && formatDate(data.fundTime, { month: 'short' }),
    data.firstFamily && `${data.firstFamily} · ${data.firstFamilyTime ? formatDate(data.firstFamilyTime, { month: 'short' }) : DASH}`,
    data.secondFamily && `${data.secondFamily} · ${data.secondFamilyTime ? formatDate(data.secondFamilyTime, { month: 'short' }) : DASH}`,
  ];
  return done[stepIndex] || undefined;
}

function JourneyStepper({ data }: { data: UserLifecycle | undefined }) {
  const current = lifecycleJourneyIndex(data?.stage);
  // Using Liminal is the end of the path: every step reads as done.
  const activeStep = current === LIFECYCLE_JOURNEY.length - 1 ? LIFECYCLE_JOURNEY.length : current;
  const offPath = !data?.stage
    ? 'The engine has not seen this user yet.'
    : data.stage === 'excluded'
      ? `Excluded${data.excludeTime ? ` since ${formatDate(data.excludeTime, { month: 'short' })}` : ''}. No nudges go out.`
      : null;

  return (
    <Card variant="elevated">
        <Typography variant="h6" sx={{ mb: 3 }}>
          Journey
        </Typography>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          // Steps not reached yet: a light node with a mid-grey number, not
          // dark text on the default mid-grey fill.
          sx={{
            '& .MuiStepIcon-root:not(.Mui-active):not(.Mui-completed)': { color: 'divider' },
            '& .MuiStepIcon-root:not(.Mui-active):not(.Mui-completed) .MuiStepIcon-text': { fill: (t) => t.palette.text.secondary },
          }}
        >
          {LIFECYCLE_JOURNEY.map((stage, i) => {
            const caption = data && i < activeStep ? stepDoneCaption(data, i) : i === current ? 'Now' : undefined;
            return (
              <Step key={stage}>
                <Tooltip title={lifecycleStageDescription(stage)} placement="top" arrow>
                  <StepLabel
                    optional={
                      caption && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {caption}
                        </Typography>
                      )
                    }
                  >
                    {lifecycleStageLabel(stage)}
                  </StepLabel>
                </Tooltip>
              </Step>
            );
          })}
        </Stepper>
        {offPath && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {offPath}
          </Typography>
        )}
    </Card>
  );
}

function errorMessage(e: unknown): string {
  return getErrorMessage(e);
}

export function NudgesSection({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useUserLifecycle(userId);
  // Time zone sets when nudges go out; the goal picks each message's version.
  const { data: user } = useUserDetail(userId);
  const restart = useRestartUserLifecycle(userId);
  const preview = useSendNudgePreview();
  const snackbar = useSnackbar();

  const [confirmRestart, setConfirmRestart] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sequence, setSequence] = useState('verify');
  const [step, setStep] = useState('0');
  const [goal, setGoal] = useState('generic');
  const [channel, setChannel] = useState<PreviewChannel>('push');

  const submitRestart = async () => {
    setRestartError(null);
    try {
      await restart.mutateAsync();
      setConfirmRestart(false);
      snackbar.show('Journey restarted. The first nudge goes out after its usual delay; see Planned nudges.');
    } catch (e) {
      setRestartError(errorMessage(e));
    }
  };

  const submitPreview = async () => {
    setPreviewError(null);
    const request: SendNudgePreviewRequest = { sequence, step: Number(step), goal, channel };
    try {
      await preview.mutateAsync(request);
      snackbar.show(`Preview sent to your account by ${channel}`);
    } catch (e) {
      setPreviewError(errorMessage(e));
    }
  };

  if (isLoading) {
    return (
      <Stack spacing={3}>
        <Card variant="elevated"><Skeleton height={100} /></Card>
        <Card variant="elevated"><Skeleton height={140} /></Card>
        <Card variant="elevated"><Skeleton height={200} /></Card>
      </Stack>
    );
  }

  const planned = data?.planned ?? [];
  const sends = data?.sends ?? [];

  return (
    <Stack spacing={3}>
      {!isError && <JourneyStepper data={data} />}

      <Card variant="elevated">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Nudge journey</Typography>
            <Button
              variant="secondary"
              size="sm"
              disabled={!data?.stage}
              onClick={() => {
                setRestartError(null);
                setConfirmRestart(true);
              }}
            >
              Restart journey
            </Button>
          </Stack>
          {isError ? (
            <FormErrorBanner error="Could not load the nudge state. Refresh to try again." />
          ) : (
            <DetailGrid>
              <DetailRow
                layout="stacked"
                label="Stage"
                value={
                  <StatusPill
                    label={lifecycleStageLabel(data?.stage)}
                    tone={lifecycleStageVariant(data?.stage)}
                    size="sm"
                  />
                }
              />
              <DetailRow layout="stacked" label="Running" value={sequenceLabel(data?.sequence)} />
              <DetailRow layout="stacked" label="Started" value={when(data?.runStartTime)} />
              <DetailRow layout="stacked" label="Paused since" value={when(data?.holdTime)} />
              <DetailRow layout="stacked" label="Last nudge" value={when(data?.lastNudgeTime)} />
              <DetailRow layout="stacked" label="Journey done for" value={sequenceLabel(data?.journeyDoneFor)} />
              <DetailRow layout="stacked" label="Verified" value={when(data?.verifyTime)} />
              <DetailRow layout="stacked" label="Funded" value={when(data?.fundTime)} />
              <DetailRow
                layout="stacked"
                label="First product"
                value={data?.firstFamily ? `${data.firstFamily} · ${when(data.firstFamilyTime)}` : DASH}
              />
              <DetailRow
                layout="stacked"
                label="Second product"
                value={data?.secondFamily ? `${data.secondFamily} · ${when(data.secondFamilyTime)}` : DASH}
              />
              <DetailRow layout="stacked" label="Excluded" value={when(data?.excludeTime)} />
              <DetailRow layout="stacked" label="Time zone" value={user?.timeZone || 'Not sent yet (UTC window)'} />
              <DetailRow layout="stacked" label="Goal" value={onboardingGoalLabel(user?.onboardingGoal)} />
            </DetailGrid>
          )}
      </Card>

      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Planned nudges
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Goes out</TableCell>
                <TableCell>Sequence</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={4}
              isLoading={false}
              isError={isError}
              isEmpty={planned.length === 0}
              emptyMessage="Nothing planned. The user has no journey running, or it is paused."
              errorMessage="Could not load planned nudges."
            >
              {planned.map((p) => (
                <TableRow key={`${p.sequence}-${p.step}`}>
                  <TableCell>{when(p.sendTime)}</TableCell>
                  <TableCell>{sequenceStepLabel(p.sequence, p.step)}</TableCell>
                  <TableCell>{CHANNEL_POLICY_LABELS[p.channelPolicy ?? ''] ?? p.channelPolicy}</TableCell>
                  <TableCell>{p.title || DASH}</TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
      </Card>

      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Sent and skipped
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Sequence</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Delivery</TableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={6}
              isLoading={false}
              isError={isError}
              isEmpty={sends.length === 0}
              emptyMessage="No nudges yet."
              errorMessage="Could not load sent nudges."
            >
              {sends.map((s) => (
                <TableRow key={`${s.sequence}-${s.step}-${s.createTime}`}>
                  <TableCell>{when(s.createTime)}</TableCell>
                  <TableCell>{sequenceStepLabel(s.sequence, s.step)}</TableCell>
                  <TableCell>
                    <StatusPill
                      label={s.outcome === 'skipped' ? `skipped: ${s.skipReason ?? ''}` : s.outcome ?? DASH}
                      tone={lifecycleOutcomeVariant(s.outcome)}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>{s.channel || DASH}</TableCell>
                  <TableCell>{s.variant || DASH}</TableCell>
                  <TableCell>{s.deliveryStatus || DASH}</TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
      </Card>

      <Card variant="elevated">
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Preview a message
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
            Sends one nudge to your own Liminal account (the one on your staff email), never to this user.
          </Typography>
          <PreviewRow>
            <Select label="Sequence" size="sm" value={sequence} onChange={setSequence} options={PREVIEW_SEQUENCES} />
            <Select label="Step" size="sm" value={step} onChange={setStep} options={PREVIEW_STEPS} />
            <Select label="Goal" size="sm" value={goal} onChange={setGoal} options={PREVIEW_GOALS} />
            <SegmentedToggle
              aria-label="Channel"
              size="sm"
              value={channel}
              onChange={setChannel}
              options={PREVIEW_CHANNELS}
            />
          </PreviewRow>
          <Stack direction="row" sx={{ mt: 2 }}>
            <Button variant="primary" size="sm" loading={preview.isPending} onClick={() => void submitPreview()}>
              Send to me
            </Button>
          </Stack>
          <Box sx={{ mt: 2 }}>
            <FormErrorBanner error={previewError ?? undefined} />
          </Box>
      </Card>

      {confirmRestart && (
        <ConfirmationDialog
          open
          title="Restart nudge journey"
          message="Starts this account's current stage journey again from step one, as if they had just reached it. Staff test accounts only."
          confirmLabel="Restart"
          loading={restart.isPending}
          onConfirm={() => void submitRestart()}
          onCancel={() => setConfirmRestart(false)}
          data-testid="restart-nudge-journey-dialog"
        >
          <FormErrorBanner error={restartError ?? undefined} />
        </ConfirmationDialog>
      )}

      <Snackbar {...snackbar.snackbarProps} />
    </Stack>
  );
}
